import { Button, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { distributeGroupMessageEmailTemplate } from 'src/shared/services/groupMessageService';
import { GroupMessageEmailTemplate } from '../models/groupMessageEmailTemplate';

type Props = {
  template: GroupMessageEmailTemplate;
  onClose: () => void;
};

export const EmailTemplateDistributeModal = ({ template, onClose }: Props) => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const associations = currentUser?.associations ?? [];

  const [selected, setSelected] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const allSelected = associations.length > 0 && selected.length === associations.length;

  const toggle = (id: number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : associations.map((a) => a.localAss_id));
  };

  const onSave = async () => {
    if (selected.length === 0) return;
    setIsSaving(true);
    try {
      await distributeGroupMessageEmailTemplate(template.id, selected);
      toast.success(<>{t('groupMessage:emailTemplates.distributeSuccess', { count: selected.length })}</>);
      onClose();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(<>{t('groupMessage:emailTemplates.distributeError')}</>);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericDialog
      open
      maxWidth='sm'
      dialogTitle={t('groupMessage:emailTemplates.distributeTitle')}
      dialogSubTitle={template.name}
      onClose={onClose}
      onSave={onSave}
      dialogContent={
        <Box>
          <Typography variant='body2' color='text.secondary' mb={2}>
            {t('groupMessage:emailTemplates.distributeDescription')}
          </Typography>
          <FormControlLabel
            label={<Typography fontWeight={600}>{t('common:form.selectAll')}</Typography>}
            control={<Checkbox checked={allSelected} indeterminate={selected.length > 0 && !allSelected} onChange={toggleAll} />}
          />
          <FormGroup sx={{ pl: 2, maxHeight: 400, overflowY: 'auto' }}>
            {associations.map((assoc) => (
              <FormControlLabel
                key={assoc.localAss_id}
                label={assoc.localAss}
                control={<Checkbox checked={selected.includes(assoc.localAss_id)} onChange={() => toggle(assoc.localAss_id)} />}
              />
            ))}
          </FormGroup>
        </Box>
      }
      dialogActions={
        <>
          <Button variant='outlined' disableElevation onClick={onClose}>
            {t('common:button.cancel')}
          </Button>
          <Button variant='contained' disableElevation disabled={isSaving || selected.length === 0} onClick={onSave}>
            {t('groupMessage:emailTemplates.distributeButton', { count: selected.length })}
          </Button>
        </>
      }
    />
  );
};
